// @flow

import React from 'react';
import { connect } from 'react-redux';
import glamorous from 'glamorous';
import { clamp, first, last } from 'lodash';
import colorUtils from 'color';
import Avatar from 'app/components/commons/Avatar';
import ChangePropsOnMount from 'app/components/commons/ChangePropsOnMount';
import getGlobalLeaderboardColor from 'app/utils/getGlobalLeaderboardColor';
import getGlobalLeaderboardScale from 'app/utils/getGlobalLeaderboardScale';
import * as cssEase from 'app/constants/cssEase';

type Props = {
  avatar: Object,
  highestScore: number,
  lowScore: number,
  name: string,
  numEntries: number,
  rank: number,
  score: number,
};

const MyScoreRowDiv = glamorous.div(props => ({
  backgroundColor: colorUtils(props.theme.color.white).alpha(0.5).string(),
  height: 40,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  borderTop: '3px solid grey',
  borderBottom: '3px solid grey',
  position: 'relative',
  fontSize: props.theme.fontSize.xsmall,
  color: props.theme.color.white,
}));

const Meter = glamorous.div(props => ({
  backgroundColor: props.color,
  height: 2,
  width: '100%',
  transform: `scaleX(${clamp(props.percent, 0, 1)}) translateZ(0)`,
  transformOrigin: 'left',
  transition: 'transform 1s',
  transitionTimingFunction: cssEase.Strong.easeOut,
}));

const UserDetails = glamorous.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const AvatarWrapper = glamorous.div({
  margin: 4,
});

const StyledAvatar = glamorous(Avatar)({
  border: '2px solid #a5a7a6',
});

const Text = glamorous.span(props => ({
  fontWeight: props.bold ? props.theme.fontWeight.bold : props.theme.fontWeight.normal,
  padding: 4,
}));

function MyScoreRow({
  avatar,
  highestScore,
  lowScore,
  name,
  numEntries,
  rank,
  score,
}: Props) {
  return (
    <MyScoreRowDiv>
      <UserDetails>
        <Text bold>#{rank}</Text>
        <AvatarWrapper>
          <StyledAvatar
            avatar={avatar}
            size="xsmall"
          />
        </AvatarWrapper>
        <Text>{name}</Text>
        <Text>-</Text>
        <Text>{score}</Text>
      </UserDetails>
      <ChangePropsOnMount
        delay={0.2}
        beforeProps={{
          percent: 0,
        }}
        afterProps={{
          percent: getGlobalLeaderboardScale(score, lowScore, highestScore),
        }}
      >
        <Meter color={getGlobalLeaderboardColor(rank, numEntries)} />
      </ChangePropsOnMount>
    </MyScoreRowDiv>
  );
}

function mapStateToProps(state) {
  const entry = state.ultimateChallenge.currentUserEntry;
  return {
    avatar: state.user.currentUser.avatar,
    highestScore: first(state.ultimateChallenge.entries).globalScore,
    lowScore: last(state.ultimateChallenge.entries).globalScore,
    name: state.user.currentUser.name,
    numEntries: state.ultimateChallenge.entries.length,
    rank: entry.position,
    score: entry.globalScore,
  };
}

export default connect(mapStateToProps)(MyScoreRow);
